import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import Seo from "@/components/base/Seo";
import { mangaList, type Manga } from "@/mocks/manga";
import { formatNum, getCategory } from "@/utils/manga";

const tabs = [
  { key: "week", label: "周榜", desc: "近7日阅读热度" },
  { key: "month", label: "月榜", desc: "本月人气趋势" },
  { key: "all", label: "高分榜", desc: "全站高分佳作" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

function sortByKey(list: Manga[], key: TabKey): Manga[] {
  const arr = [...list];
  if (key === "week") return arr.sort((a, b) => b.hot - a.hot);
  if (key === "month") return arr.sort((a, b) => b.popularity - a.popularity);
  return arr.sort((a, b) => b.rating - a.rating);
}

const topColors = [
  "bg-primary-500",
  "bg-secondary-500",
  "bg-accent-500",
  "bg-foreground-400",
  "bg-foreground-400",
  "bg-foreground-400",
];

export default function Rankings() {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const [tab, setTab] = useState<TabKey>("month");

  const list = useMemo(() => {
    let base = sortByKey(mangaList, tab);
    if (q) {
      const kw = q.toLowerCase();
      base = base.filter((m) =>
        [m.title, m.author, ...m.tags].some((t) => t.toLowerCase().includes(kw))
      );
    }
    return base;
  }, [tab, q]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "歪歪漫画网 人气排行榜",
      url: "https://panelpal.github.io/rankings",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://panelpal.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "人气排行榜",
          item: "https://panelpal.github.io/rankings",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: list.slice(0, 10).map((m, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: m.title,
      })),
    },
  ];

  return (
    <MainLayout>
      <Seo
        title="歪歪漫画网人气排行榜 - 周榜 · 月榜 · 高分榜"
        description="歪歪漫画网人气排行榜实时更新，周榜、月榜、高分榜一键切换。查看恋爱、热血、悬疑、治愈等全题材当下最热门的漫画作品与阅读趋势。"
        keywords="歪歪漫画网人气排行,热门榜单,月榜,周榜,高分榜,漫画排名"
        canonical="/rankings"
        jsonLd={jsonLd}
      />

      {/* 页头 */}
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">人气排行榜</span>
          </nav>
          <div className="mt-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-fire-line" /> 实时榜单
              </span>
              <h1 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">人气排行榜</h1>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                歪歪漫画网人气排行与热门榜单实时更新，覆盖恋爱、热血、悬疑、治愈等全题材，帮你发现当下最受读者喜爱的漫画。
              </p>
            </div>
            {q && (
              <span className="rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-sm text-primary-700">
                关键词：“{q}” 共 {list.length} 部
              </span>
            )}
          </div>
        </div>
      </section>

      {/* 榜单主体 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        {/* tab */}
        <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors sm:flex-none sm:px-6 ${
                tab === t.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {t.label}
            </button>
          ))}
          <span className="hidden flex-1 pr-4 text-right text-xs text-foreground-400 sm:block">
            {tabs.find((t) => t.key === tab)?.desc}
          </span>
        </div>

        {/* 榜单列表 */}
        {list.length === 0 ? (
          <div className="mt-10 rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <p className="text-foreground-600">没有找到符合条件的结果，换个关键词试试吧。</p>
          </div>
        ) : (
          <ol className="mt-6 grid grid-cols-1 gap-3">
            {list.map((m, i) => {
              const cat = getCategory(m.category);
              return (
                <li key={m.id}>
                  <Link
                    to={`/manga/${m.id}`}
                    className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-sm sm:gap-5"
                  >
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-heading text-lg text-background-50 ${
                        topColors[i] || "bg-foreground-300 text-foreground-700"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <img
                      src={m.cover}
                      alt={`第${i + 1}名 ${m.title} 封面`}
                      loading="lazy"
                      className="h-20 w-14 shrink-0 rounded-md object-cover sm:h-24 sm:w-[4.5rem]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-base font-semibold text-foreground-950">{m.title}</span>
                        {m.isFree && (
                          <span className="rounded-md bg-primary-500 px-1.5 py-0.5 text-xs font-medium text-background-50">
                            免费
                          </span>
                        )}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
                        <span>{cat.name}</span>
                        <span>·</span>
                        <span>{m.status}</span>
                        <span>·</span>
                        <span>{m.author}</span>
                        <span>·</span>
                        <span>{m.chapters}话</span>
                      </span>
                      <span className="mt-1.5 flex flex-wrap gap-1.5">
                        {m.tags.map((t) => (
                          <span key={t} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
                            {t}
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                      <span className="flex items-center gap-1 text-sm font-semibold text-primary-600">
                        <i className="ri-star-fill text-secondary-500" />
                        {m.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-fire-line" /> 人气 {formatNum(m.popularity)}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </MainLayout>
  );
}